import React, { ReactNode, useState } from 'react'
import {
  BottomDrawer,
  breakpointQueries,
  Button,
  InputGroup,
  Modal,
  useMediaQuery,
} from '@systemeio/components'
import { XCloseIcon } from '@systemeio/icons'
import { useTranslation } from 'next-i18next'

export const LinkModal = ({
  linkIsEditing,
  setLinkIsEditing,
  onInsertLink,
}: {
  linkIsEditing: boolean
  setLinkIsEditing: (value: boolean) => void
  onInsertLink: (url: string) => void
}): ReactNode => {
  const { t } = useTranslation()
  const [url, setUrl] = useState('')
  const isMobile = useMediaQuery(breakpointQueries.isMobile, { initializeWithValue: false })

  const handleSaveLink = (): void => {
    if (url) {
      onInsertLink(url)
    }
    setLinkIsEditing(false)
    setUrl('')
  }
  const closeOnEnterKey = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      handleSaveLink()
    }
  }

  const onCloseDialog = (): void => {
    setLinkIsEditing(false)
    setUrl('')
  }

  const title = t('comments.components.comments.add_link_title')

  const fields = (
    <InputGroup.Root className="w-full">
      <InputGroup.Input
        placeholder={t('comments.components.comments.link_menu_placeholder')}
        onKeyDown={closeOnEnterKey}
        value={url}
        onChange={e => setUrl(e.target.value)}
      />
    </InputGroup.Root>
  )

  const renderAction = (buttonClassName?: string): ReactNode => (
    <>
      <Button variant="secondary" onClick={onCloseDialog} className={buttonClassName}>
        {t('comments.components.comments.add_link_cancel')}
      </Button>
      <Button variant="primary" onClick={handleSaveLink} className={buttonClassName}>
        {t('comments.components.comments.add_link_save')}
      </Button>
    </>
  )

  const action = renderAction()

  return isMobile ? (
    <BottomDrawer.Root
      open={linkIsEditing}
      onOpenChange={open => {
        if (!open) {
          onCloseDialog()
        }
      }}
    >
      <BottomDrawer.Popup>
        <BottomDrawer.Header className="flex-row items-start gap-2.5">
          <BottomDrawer.Title className="min-w-0 flex-1">{title}</BottomDrawer.Title>
          <BottomDrawer.Close
            aria-label="Close"
            className="shrink-0"
            render={<Button variant="ghost-gray" size="icon-sm" />}
          >
            <XCloseIcon />
          </BottomDrawer.Close>
        </BottomDrawer.Header>
        <BottomDrawer.Content className="flex flex-col gap-6">{fields}</BottomDrawer.Content>
        <BottomDrawer.Footer>
          <div className="flex w-full gap-3">{renderAction('min-w-[90px] flex-1')}</div>
        </BottomDrawer.Footer>
      </BottomDrawer.Popup>
    </BottomDrawer.Root>
  ) : (
    <Modal.Root
      open={linkIsEditing}
      onOpenChange={open => {
        if (!open) {
          onCloseDialog()
        }
      }}
    >
      <Modal.Popup>
        <Modal.Header className="pr-16">
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-8 py-6">{fields}</div>
        <Modal.Footer>{action}</Modal.Footer>
      </Modal.Popup>
    </Modal.Root>
  )
}
